/**
 * Collapsible list of registered FCM devices with per-device push
 * enable/disable toggles and removal.
 */

import React from 'react';
import type { FCMTokenEntry } from '../../../hooks/useFCMToken';
import { getDeviceIcon, formatRegistrationDate } from '../utils/browserInfo';

interface DevicesSectionProps {
  fcmTokens: FCMTokenEntry[];
  currentDeviceId: string | null;
  expanded: boolean;
  onToggleExpanded: () => void;
  pendingToggleDeviceId: string | null;
  onDeviceToggle: (deviceId: string, enabled: boolean) => void;
  onRequestRemove: (deviceId: string) => void;
}

export const DevicesSection: React.FC<DevicesSectionProps> = ({
  fcmTokens,
  currentDeviceId,
  expanded,
  onToggleExpanded,
  pendingToggleDeviceId,
  onDeviceToggle,
  onRequestRemove,
}) => {
  return (
    <div className="bg-surface-muted rounded-lg border border-line overflow-hidden">
      <button
        onClick={onToggleExpanded}
        className="w-full flex items-center gap-3 p-4 hover:bg-surface-hover transition-colors"
      >
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-hue-indigo-soft text-hue-indigo-deep">
          <i className="fa-solid fa-laptop"></i>
        </div>
        <div className="flex-1 text-left">
          <span className="font-semibold text-ink">Devices</span>
          <p className="text-xs text-ink-muted">
            {fcmTokens.filter(d => d.enabled !== false).length} of {fcmTokens.length} {fcmTokens.length === 1 ? 'device' : 'devices'} enabled for push
          </p>
        </div>
        <i className={`fa-solid fa-chevron-down text-ink-faint transition-transform ${expanded ? 'rotate-180' : ''}`}></i>
      </button>

      {expanded && (
      <div className="p-4 pt-0 space-y-3">
        {fcmTokens.map((device) => {
          const isCurrentDevice = device.deviceId === currentDeviceId;
          const isPendingToggle = pendingToggleDeviceId === device.deviceId;

          return (
            <div
              key={device.deviceId}
              className={`bg-surface rounded-lg border p-3 ${
                isCurrentDevice ? 'border-hue-indigo-border-strong ring-1 ring-hue-indigo-border-subtle' : 'border-line'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Device Icon */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  device.enabled !== false ? 'bg-hue-indigo-soft text-hue-indigo-deep' : 'bg-surface-inset text-ink-faint'
                }`}>
                  <i className={`fa-brands ${getDeviceIcon(device.userAgent)} text-lg`}></i>
                </div>

                {/* Device Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium truncate ${
                      device.enabled !== false ? 'text-ink' : 'text-ink-muted line-through'
                    }`}>
                      {device.deviceName || 'Unknown Device'}
                    </span>
                    {isCurrentDevice && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-hue-indigo-soft text-hue-indigo-deep">
                        This device
                      </span>
                    )}
                    {device.enabled === false && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface-strong text-ink-body">
                        Disabled
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-ink-muted mt-0.5">
                    Registered {formatRegistrationDate(device.registeredAt)}
                  </p>
                </div>

                {/* Toggle & Remove */}
                <div className="flex items-center gap-2">
                  {/* Enable/Disable Toggle */}
                  <button
                    onClick={() => onDeviceToggle(device.deviceId, device.enabled === false)}
                    disabled={isPendingToggle}
                    title={device.enabled !== false ? 'Disable push for this device' : 'Enable push for this device'}
                    className={`relative w-10 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-1 ${
                      device.enabled !== false ? 'bg-indigo-600' : 'bg-surface-deep'
                    } ${isPendingToggle ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    {isPendingToggle ? (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <i className="fa-solid fa-circle-notch fa-spin text-ink-inverse text-xs"></i>
                      </span>
                    ) : (
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-surface rounded-full shadow-sm transition-transform ${
                          device.enabled !== false ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    )}
                  </button>

                  {/* Remove Button */}
                  <button
                    onClick={() => onRequestRemove(device.deviceId)}
                    title="Remove device"
                    className="p-1.5 text-ink-faint hover:text-danger hover:bg-danger-subtle rounded transition-colors"
                  >
                    <i className="fa-solid fa-trash-can text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        <p className="text-xs text-ink-muted mt-3">
          <i className="fa-solid fa-info-circle mr-1"></i>
          Disabled devices won't receive push notifications but will still see in-app alerts.
        </p>
      </div>
      )}
    </div>
  );
};
